// src/hooks/useSessionTimeout.ts
// Monitors user inactivity and auto-logs out after configurable timeout.
// Shows a warning modal VITE_SESSION_WARNING seconds before logout.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthStore } from '../features/auth/auth.store';
import { useSettingsStore } from '../features/settings/settings.store';

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove'];

export function useSessionTimeout() {
  const { isAuthenticated, logout } = useAuthStore();
  const sessionTimeoutSeconds = useSettingsStore((s) => s.sessionTimeoutSeconds);
  const sessionWarningSeconds = useSettingsStore((s) => s.sessionWarningSeconds);
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(sessionWarningSeconds);

  const logoutTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const warningTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const countdownInterval = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  const clearAllTimers = useCallback(() => {
    clearTimeout(logoutTimer.current);
    clearTimeout(warningTimer.current);
    clearInterval(countdownInterval.current);
  }, []);

  const doLogout = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    logout();
  }, [clearAllTimers, logout]);

  const startTimers = useCallback(() => {
    clearAllTimers();
    if (!isAuthenticated) return;

    const warningDelay = (sessionTimeoutSeconds - sessionWarningSeconds) * 1000;
    const logoutDelay = sessionTimeoutSeconds * 1000;

    warningTimer.current = setTimeout(() => {
      setShowWarning(true);
      setSecondsLeft(sessionWarningSeconds);
      countdownInterval.current = setInterval(() => {
        setSecondsLeft((s) => {
          if (s <= 1) return 0;
          return s - 1;
        });
      }, 1000);
    }, warningDelay);

    logoutTimer.current = setTimeout(doLogout, logoutDelay);
  }, [isAuthenticated, clearAllTimers, doLogout, sessionTimeoutSeconds, sessionWarningSeconds]);

  const staySignedIn = useCallback(() => {
    setShowWarning(false);
    clearInterval(countdownInterval.current);
    startTimers();
  }, [startTimers]);

  // Restart timers on user activity
  useEffect(() => {
    if (!isAuthenticated) {
      clearAllTimers();
      setShowWarning(false);
      return;
    }

    startTimers();

    const handleActivity = () => {
      if (!showWarning) startTimers();
    };

    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, handleActivity, { passive: true })
    );

    return () => {
      clearAllTimers();
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, handleActivity)
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Listen for global 401 (token expired server-side)
  useEffect(() => {
    const handle = () => doLogout();
    window.addEventListener('hams:unauthorized', handle);
    return () => window.removeEventListener('hams:unauthorized', handle);
  }, [doLogout]);

  return { showWarning, secondsLeft, staySignedIn, doLogout };
}
